/**
 * Settings.tsx - 系统设置页面
 * 管理系统配置、消息模板、Webhook、调度设置等
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  InputNumber,
  Switch,
  Button,
  Space,
  message,
  Select,
  Table,
  Modal,
  Tag,
  Alert,
  Divider,
  Typography,
  Row,
  Col,
  Collapse,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  SendOutlined,
  EditOutlined,
  CopyOutlined,
  CheckOutlined,
  SettingOutlined,
  MailOutlined,
  TwitterOutlined,
  BellOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  CodeOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { configApi, systemApi, userConfigApi } from '../api/client';
import type { UserConfig } from '../api/client';
import { useAppStore, useAuthStore } from '../store';
import dayjs from 'dayjs';
import { TemplateEditor } from '../components/TemplateEditor';
import { StrategyConfig } from '../components/StrategyConfig';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Option } = Select;

// 模板变量说明
const templateVariables = [
  { name: '{{project_name}}', desc: '项目名称' },
  { name: '{{project_website}}', desc: '项目官网' },
  { name: '{{project_twitter}}', desc: '项目Twitter' },
  { name: '{{project_category}}', desc: '项目类别' },
  { name: '{{project_chain}}', desc: '所在链' },
  { name: '{{project_tvl}}', desc: 'TVL数值' },
  { name: '{{sender_name}}', desc: '发送者名称' },
  { name: '{{sender_title}}', desc: '发送者职位' },
  { name: '{{company_name}}', desc: '公司名称' },
  { name: '{{date}}', desc: '当前日期' },
];

const Settings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<any>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState<any>(null);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [userConfig, setUserConfig] = useState<UserConfig | null>(null);

  const [configForm] = Form.useForm();
  const [templateForm] = Form.useForm();
  const [webhookForm] = Form.useForm();

  const { systemConfig, setSystemConfig } = useAppStore();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  // 加载配置
  const loadConfig = async () => {
    setLoading(true);
    try {
      const [configData, templatesData, userConfigData] = await Promise.all([
        configApi.get(),
        configApi.getTemplates(),
        userConfigApi.get().catch(() => null),
      ]);
      setConfig(configData);
      setTemplates(templatesData.items || []);
      setUserConfig(userConfigData);
      configForm.setFieldsValue(configData);
      setSystemConfig(configData);
    } catch (error: any) {
      message.error(`加载配置失败: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  // 保存配置
  const handleSaveConfig = async (values: any) => {
    setSaving(true);
    try {
      await configApi.update(values);
      message.success('配置已保存');
      setSystemConfig(values);
    } catch (error: any) {
      message.error(`保存失败: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // 保存模板
  const handleSaveTemplate = async (values: any) => {
    setSaving(true);
    try {
      await configApi.updateTemplate(editingTemplate.id, values);
      message.success('模板已保存');
      setTemplateModalVisible(false);
      loadConfig();
    } catch (error: any) {
      message.error(`保存失败: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // 测试Webhook
  const handleTestWebhook = async () => {
    const url = webhookForm.getFieldValue('webhook_url');
    if (!url) {
      message.warning('请先输入Webhook URL');
      return;
    }

    setTestingWebhook(true);
    setWebhookTestResult(null);
    try {
      const result = await configApi.testWebhook(url);
      setWebhookTestResult(result);
      if (result.success) {
        message.success('Webhook测试成功');
      } else {
        message.error(`Webhook测试失败: ${result.error}`);
      }
    } catch (error: any) {
      setWebhookTestResult({ success: false, error: error.message });
      message.error(`测试失败: ${error.message}`);
    } finally {
      setTestingWebhook(false);
    }
  };

  // 触发手动抓取
  const handleTriggerScrape = async () => {
    try {
      const result = await systemApi.triggerScrape();
      message.success(`抓取任务已启动，任务ID: ${result.task_id}`);
    } catch (error: any) {
      message.error(`启动失败: ${error.message}`);
    }
  };

  // 编辑模板
  const openTemplateEditor = (template: any) => {
    setEditingTemplate(template);
    templateForm.setFieldsValue(template);
    setTemplateModalVisible(true);
  };

  // 复制模板变量
  const copyVariable = (variable: string) => {
    navigator.clipboard.writeText(variable);
    message.success('已复制到剪贴板');
  };

  // 模板列表列
  const templateColumns = [
    {
      title: '模板名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '渠道',
      dataIndex: 'channel',
      key: 'channel',
      width: 100,
      render: (channel: string) => {
        const channelConfig: Record<string, { color: string; label: string }> = {
          email: { color: 'blue', label: 'Email' },
          twitter: { color: 'cyan', label: 'Twitter' },
          telegram: { color: 'purple', label: 'Telegram' },
        };
        const config = channelConfig[channel] || { color: 'default', label: channel };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '阶段',
      dataIndex: 'phase',
      key: 'phase',
      width: 100,
      render: (phase: number) => <Tag>Phase {phase}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active: boolean) => (
        <Tag color={active ? 'success' : 'default'}>
          {active ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '最后修改',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      render: (val: string) => val ? dayjs(val).format('MM-DD HH:mm') : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_: any, record: any) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => openTemplateEditor(record)}
        >
          编辑
        </Button>
      ),
    },
  ];

  return (
    <div className="settings-page">
      <Card loading={loading}>
        <Tabs defaultActiveKey={isAdmin ? "general" : "strategy"}>
          {/* 通用设置 (Admin only) */}
          {isAdmin && <TabPane
            tab={<span><SettingOutlined /> 系统配置</span>}
            key="general"
          >
            <Form
              form={configForm}
              layout="vertical"
              onFinish={handleSaveConfig}
              style={{ maxWidth: 800 }}
            >
              <Title level={5}>基本信息</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={['sender', 'name']}
                    label="发送者名称"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="如：John Smith" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['sender', 'title']}
                    label="发送者职位"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="如：Business Development" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={['sender', 'company']}
                    label="公司名称"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="TaskOn" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['sender', 'email']}
                    label="发送邮箱"
                    rules={[{ type: 'email' }]}
                  >
                    <Input placeholder="bd@taskon.xyz" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={5}>抓取设置</Title>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name={['scraper', 'daily_limit']}
                    label="每日抓取上限"
                  >
                    <InputNumber min={10} max={1000} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={['scraper', 'min_tvl']}
                    label="最小TVL筛选"
                  >
                    <InputNumber min={0} style={{ width: '100%' }} addonAfter="USD" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={['scraper', 'category_filter']}
                    label="类别筛选"
                  >
                    <Select mode="multiple" placeholder="选择类别">
                      <Option value="defi">DeFi</Option>
                      <Option value="dex">DEX</Option>
                      <Option value="perps">Perps</Option>
                      <Option value="lending">Lending</Option>
                      <Option value="bridge">Bridge</Option>
                      <Option value="yield">Yield</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={5}>触达设置</Title>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name={['outreach', 'daily_email_limit']}
                    label="每日Email上限"
                  >
                    <InputNumber min={1} max={500} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={['outreach', 'daily_twitter_limit']}
                    label="每日Twitter上限"
                  >
                    <InputNumber min={1} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={['outreach', 'daily_telegram_limit']}
                    label="每日Telegram上限"
                  >
                    <InputNumber min={1} max={200} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name={['outreach', 'phase1_delay_days']}
                    label="Phase1间隔(天)"
                  >
                    <InputNumber min={0} max={30} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={['outreach', 'phase2_delay_days']}
                    label="Phase2间隔(天)"
                  >
                    <InputNumber min={1} max={30} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={['outreach', 'phase3_delay_days']}
                    label="Phase3间隔(天)"
                  >
                    <InputNumber min={1} max={30} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item style={{ marginTop: 24 }}>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={saving}
                  >
                    保存配置
                  </Button>
                  <Button icon={<ReloadOutlined />} onClick={loadConfig}>
                    重新加载
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </TabPane>}

          {/* 触达策略配置 (BD: 个人设置) */}
          <TabPane
            tab={<span><ClockCircleOutlined /> {isAdmin ? '触达策略' : '个人设置'}</span>}
            key="strategy"
          >
            <StrategyConfig onSave={loadConfig} />
          </TabPane>

          {/* 消息模板 - 使用新的TemplateEditor组件 */}
          <TabPane
            tab={<span><MailOutlined /> 话术模板</span>}
            key="templates"
          >
            <TemplateEditor
              userConfig={userConfig || undefined}
              onSave={() => loadConfig()}
            />
          </TabPane>

          {/* Webhook设置 (Admin only) */}
          {isAdmin && <TabPane
            tab={<span><BellOutlined /> Webhook通知</span>}
            key="webhook"
          >
            <Form
              form={webhookForm}
              layout="vertical"
              style={{ maxWidth: 800 }}
              initialValues={config?.webhook}
            >
              <Form.Item
                name="webhook_url"
                label="Webhook URL"
                extra="当有新回复或重要事件时，系统会发送POST请求到此URL"
              >
                <Input placeholder="https://your-server.com/webhook" />
              </Form.Item>

              <Form.Item
                name="webhook_events"
                label="触发事件"
              >
                <Select mode="multiple" placeholder="选择要通知的事件">
                  <Option value="reply_received">收到回复</Option>
                  <Option value="interested_reply">意向回复</Option>
                  <Option value="meeting_request">会议请求</Option>
                  <Option value="outreach_sent">触达发送</Option>
                  <Option value="outreach_failed">发送失败</Option>
                  <Option value="project_scored">项目评分完成</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="webhook_secret"
                label="签名密钥"
                extra="用于验证Webhook请求的HMAC签名"
              >
                <Input.Password placeholder="可选，用于请求签名验证" />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    icon={<SendOutlined />}
                    onClick={handleTestWebhook}
                    loading={testingWebhook}
                  >
                    测试Webhook
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => handleSaveConfig(webhookForm.getFieldsValue())}
                    loading={saving}
                  >
                    保存设置
                  </Button>
                </Space>
              </Form.Item>

              {webhookTestResult && (
                <Alert
                  message={webhookTestResult.success ? '测试成功' : '测试失败'}
                  description={
                    webhookTestResult.success
                      ? `响应状态: ${webhookTestResult.status_code}, 耗时: ${webhookTestResult.response_time}ms`
                      : webhookTestResult.error
                  }
                  type={webhookTestResult.success ? 'success' : 'error'}
                  showIcon
                />
              )}
            </Form>
          </TabPane>}

          {/* 调度设置 (Admin only) */}
          {isAdmin && <TabPane
            tab={<span><ClockCircleOutlined /> 调度设置</span>}
            key="scheduler"
          >
            <Form
              layout="vertical"
              style={{ maxWidth: 800 }}
              initialValues={config?.scheduler}
              onFinish={(values) => handleSaveConfig({ scheduler: values })}
            >
              <Title level={5}>自动抓取</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="scrape_enabled"
                    label="启用自动抓取"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="scrape_cron"
                    label="抓取时间（Cron表达式）"
                    extra="默认：每天凌晨2点"
                  >
                    <Input placeholder="0 2 * * *" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={5}>自动触达</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="outreach_enabled"
                    label="启用自动触达"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="outreach_hours"
                    label="触达时段"
                    extra="设置工作日触达的小时范围"
                  >
                    <Select mode="multiple" placeholder="选择小时">
                      {Array.from({ length: 24 }, (_, i) => (
                        <Option key={i} value={i}>
                          {i.toString().padStart(2, '0')}:00
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={5}>回复监控</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="reply_monitor_enabled"
                    label="启用回复监控"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="reply_check_interval"
                    label="检查间隔（分钟）"
                  >
                    <InputNumber min={5} max={60} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item style={{ marginTop: 24 }}>
                <Space>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                    保存调度设置
                  </Button>
                  <Button icon={<PlayCircleOutlined />} onClick={handleTriggerScrape}>
                    立即执行抓取
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </TabPane>}

          {/* 安全设置 (Admin only) */}
          {isAdmin && <TabPane
            tab={<span><SafetyOutlined /> 安全设置</span>}
            key="security"
          >
            <Form
              layout="vertical"
              style={{ maxWidth: 800 }}
              initialValues={config?.security}
              onFinish={(values) => handleSaveConfig({ security: values })}
            >
              <Title level={5}>API访问</Title>
              <Form.Item
                name="api_key"
                label="API密钥"
                extra="用于API认证，请妥善保管"
              >
                <Input.Password placeholder="自动生成的API密钥" />
              </Form.Item>

              <Form.Item
                name="allowed_ips"
                label="IP白名单"
                extra="留空则不限制，多个IP用逗号分隔"
              >
                <Input placeholder="如：192.168.1.1, 10.0.0.0/8" />
              </Form.Item>

              <Divider />

              <Title level={5}>限流设置</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="rate_limit_per_minute"
                    label="每分钟请求上限"
                  >
                    <InputNumber min={10} max={1000} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="rate_limit_per_day"
                    label="每日请求上限"
                  >
                    <InputNumber min={100} max={100000} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item style={{ marginTop: 24 }}>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                  保存安全设置
                </Button>
              </Form.Item>
            </Form>
          </TabPane>}
        </Tabs>
      </Card>

      {/* 模板编辑弹窗 */}
      <Modal
        title={`编辑模板: ${editingTemplate?.name || ''}`}
        open={templateModalVisible}
        onCancel={() => setTemplateModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={templateForm}
          layout="vertical"
          onFinish={handleSaveTemplate}
        >
          <Form.Item
            name="name"
            label="模板名称"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          {editingTemplate?.channel === 'email' && (
            <Form.Item
              name="subject"
              label="邮件主题"
              rules={[{ required: true }]}
            >
              <Input placeholder="支持使用模板变量" />
            </Form.Item>
          )}

          <Form.Item
            name="content"
            label="模板内容"
            rules={[{ required: true }]}
          >
            <TextArea
              rows={12}
              placeholder="支持使用模板变量，如 {{project_name}}"
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="启用状态"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setTemplateModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                保存模板
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Settings;
